/**
 * Felddefinition des Baustellenberichts („PROBIG Baustelle").
 *
 * **Eine** Quelle für drei Verbraucher — Formular, Validierung und PDF. Vorbild
 * ist `QM_STEPS` im QM-Modul (`qm/qm-process.schema.ts`), das dort UI und PDF
 * aus einer Konstante speist; der Bericht braucht zusätzlich Feldtypen, weil er
 * nicht nur Textfelder hat.
 *
 * Ein neues Feld = eine Zeile hier + eine Spalte in der Migration. Eine neue
 * Auswahloption = eine Zeile hier, **ohne** Migration (die Optionen liegen
 * bewusst nicht als Prisma-Enum in der DB).
 *
 * Nicht hier: alles, was schon am Projekt steht (Auftraggeber, Ansprechpartner,
 * Projektleitung, Anzahl Becken, Maße, Wasserspiegel, Anlagentyp,
 * Kommunal/Industrie). Das liest der Bericht live aus dem Projekt (§6).
 */

/** Wie ein Feld erfasst und im PDF dargestellt wird. */
export type ReportFieldType =
  | 'text'
  | 'longtext'
  | 'number'
  | 'date'
  /** Dreiwertig: ja / nein / offen — wie `ProjectChecklistItem.isYes`. */
  | 'bool3'
  /** Genau eine Auswahl. */
  | 'enum'
  /** Mehrfachauswahl (Array-Spalte). */
  | 'multi';

export interface ReportFieldOption {
  value: string;
  label: string;
}

export interface ReportField {
  /** Spaltenname am `AssemblyReport` (camelCase). */
  key: string;
  label: string;
  type: ReportFieldType;
  /** Nur bei `enum`/`multi`. */
  options?: readonly ReportFieldOption[];
  /** Einheit hinter dem Eingabefeld, z. B. „km". */
  unit?: string;
  /**
   * Zahl ohne Tausendertrennung ausgeben — für Jahreszahlen und Nummern,
   * sonst würde aus 1998 ein „1.998".
   */
  noGrouping?: boolean;
  /**
   * Ganzzahl. **Muss** gesetzt sein, wenn die Spalte in Prisma `Int` ist —
   * sonst nimmt Zod „2,5" an und Prisma wirft beim Schreiben, aus einem
   * sauberen 422 würde ein 500.
   */
  isInteger?: boolean;
  /** Hilfetext — Platzhalter im Formular, keine Vorbelegung. */
  hint?: string;
  /**
   * Feld nur zeigen, wenn die Torfrage passt (reine **Anzeige**-Regel, siehe
   * `isFieldVisible`). Beispiel: den Ort der Unterkunft fragt man nicht, wenn es
   * keine Unterkunft gab. Bei einer Wartung ohne Übernachtung blockierten diese
   * Felder sonst dauerhaft den Ausfüllstand.
   *
   * **Wichtig:** Das PDF druckt trotzdem JEDEN gesetzten Wert. Ändert sich die
   * Torfrage nachträglich, verschwindet nur das Eingabefeld — der bereits
   * erfasste Wert bleibt erhalten und im Dokument sichtbar.
   */
  visibleWhen?: {
    /** Schlüssel der Torfrage. */
    key: string;
    /** Sichtbar, solange die Torfrage KEINEN dieser Werte hat. */
    notIn?: readonly unknown[];
    /** Sichtbar nur, wenn die Torfrage überhaupt beantwortet ist. */
    isSet?: true;
  };
  /**
   * Angabe, die das Büro liefert (Betreiberwissen: seit wann in Betrieb, letzte
   * Wartung, Betriebsstunden …). Wird angezeigt, zählt aber **nicht** in den
   * Ausfüllstand — der Monteur auf der Baustelle kann sie nicht beantworten und
   * käme nie auf „fertig".
   */
  isOffice?: boolean;
}

/**
 * Ist das Feld nach seiner Torfrage gerade zu zeigen? Eine Quelle für Formular
 * UND Ausfüllstand — sonst zählt der Balken Felder mit, die niemand sieht.
 */
export function isFieldVisible(field: ReportField, values: Record<string, unknown>): boolean {
  const rule = field.visibleWhen;
  if (!rule) return true;
  const gate = values[rule.key] ?? null;
  if (rule.isSet && (gate === null || gate === '')) return false;
  if (rule.notIn && rule.notIn.includes(gate)) return false;
  return true;
}

export interface ReportSection {
  key: string;
  title: string;
  /** Kurzer Hinweis unter der Abschnittsüberschrift. */
  description?: string;
  fields: readonly ReportField[];
}

const ARRIVAL_MODES: readonly ReportFieldOption[] = [
  { value: 'AUTO', label: 'Auto' },
  { value: 'ZUG', label: 'Zug' },
  { value: 'FLUG', label: 'Flug' },
  { value: 'MIETWAGEN', label: 'Mietwagen' },
];

const ACCOMMODATION_BY: readonly ReportFieldOption[] = [
  { value: 'PROBIG', label: 'durch PROBIG' },
  { value: 'KUNDE', label: 'durch Kunde' },
  { value: 'KEINE', label: 'keine Unterkunft' },
];

const PURPOSES: readonly ReportFieldOption[] = [
  { value: 'DEMONTAGE', label: 'Demontage' },
  { value: 'MONTAGE', label: 'Montage' },
  { value: 'UMRUESTUNG', label: 'Umrüstung' },
  { value: 'WARTUNG', label: 'Wartung' },
  { value: 'ABNAHME', label: 'Abnahme' },
  { value: 'BESTANDSAUFNAHME', label: 'Bestandsaufnahme' },
  { value: 'SCHADENSAUFSTELLUNG', label: 'Schadensaufstellung' },
];

// ── Technik vor Ort (Block D, Rest) ─────────────────────────────────────────

/** Kettentyp des Räumers. Die beiden Bauarten, die PROBIG verbaut. */
const CHAIN_TYPES: readonly ReportFieldOption[] = [
  { value: 'NCS', label: 'NCS' },
  { value: 'LOOP_CHAIN', label: 'Loop Chain' },
  { value: 'ANDERE', label: 'andere' },
];

/** Was im Becken steht. `projectGroup` kennt nur Kommunal/Industrie. */
const MEDIA: readonly ReportFieldOption[] = [
  { value: 'ABWASSER', label: 'Abwasser' },
  { value: 'REGENWASSER', label: 'Regenwasser' },
  { value: 'PROZESSWASSER', label: 'Prozesswasser' },
  { value: 'TRINKWASSER', label: 'Trinkwasser' },
  { value: 'SCHLAMM', label: 'Schlamm' },
  { value: 'ANDERES', label: 'anderes' },
];

// ── Zustand & Umgebung (Blöcke E/F) ─────────────────────────────────────────

const BASIN_SHAPES: readonly ReportFieldOption[] = [
  { value: 'RECHTECK', label: 'Rechteck' },
  { value: 'RUND', label: 'Rund' },
  { value: 'OVAL', label: 'Oval' },
  { value: 'ANDERE', label: 'andere' },
];

const BUILD_TYPES: readonly ReportFieldOption[] = [
  { value: 'NEUBAU', label: 'Neubau' },
  { value: 'UMBAU', label: 'Umbau' },
  { value: 'SANIERUNG', label: 'Sanierung' },
];

const BASIN_MATERIALS: readonly ReportFieldOption[] = [
  { value: 'BETON', label: 'Beton' },
  { value: 'STAHL', label: 'Stahl' },
  { value: 'EDELSTAHL', label: 'Edelstahl' },
  { value: 'KUNSTSTOFF', label: 'Kunststoff' },
  { value: 'BESCHICHTET', label: 'beschichtet' },
];

/** Wie sauber das Becken bei Arbeitsbeginn war — bestimmt den Aufwand. */
const CLEANLINESS: readonly ReportFieldOption[] = [
  { value: 'SAUBER', label: 'sauber' },
  { value: 'LEICHT_VERSCHMUTZT', label: 'leicht verschmutzt' },
  { value: 'STARK_VERSCHMUTZT', label: 'stark verschmutzt' },
];

/**
 * Die Abschnitte in Erfassungs- und PDF-Reihenfolge. Weitere Blöcke der Vorlage
 * (Baustellenverlauf, Becken/Zustand, Listen) folgen in späteren Schritten.
 */
export const REPORT_SECTIONS: readonly ReportSection[] = [
  {
    key: 'kopf',
    title: 'Einsatz',
    description: 'Auftraggeber, Projektleitung und Beckendaten stehen am Projekt und werden oben angezeigt.',
    fields: [
      { key: 'totalKm', label: 'Gefahrene Kilometer (gesamt)', type: 'number', unit: 'km' },
      {
        key: 'summary',
        label: 'Fazit',
        type: 'longtext',
        hint: 'Kurzes Gesamtfazit des Einsatzes.',
      },
    ],
  },
  {
    key: 'zweck',
    title: 'Zweck des Besuchs',
    fields: [{ key: 'purposes', label: 'Zweck', type: 'multi', options: PURPOSES }],
  },
  {
    key: 'anreise',
    title: 'Anreise & Unterkunft',
    fields: [
      { key: 'arrivalModes', label: 'Anreise', type: 'multi', options: ARRIVAL_MODES },
      { key: 'accommodationBy', label: 'Unterkunft', type: 'enum', options: ACCOMMODATION_BY },
      {
        key: 'accommodationPlace',
        label: 'Ort der Unterkunft',
        type: 'text',
        visibleWhen: { key: 'accommodationBy', isSet: true, notIn: ['KEINE'] },
      },
      {
        key: 'accommodationDistanceKm',
        label: 'Entfernung zur Baustelle',
        type: 'number',
        unit: 'km',
        visibleWhen: { key: 'accommodationBy', isSet: true, notIn: ['KEINE'] },
      },
    ],
  },
  {
    key: 'sicherheit',
    title: 'Sicherheit',
    fields: [
      { key: 'siteAuditAt', label: 'Baustellenaudit am', type: 'date' },
      // Fand keine Unterweisung statt, sind Datum und Unterweiser gegenstandslos.
      {
        key: 'safetyBriefingAt',
        label: 'Unterweisung am',
        type: 'date',
        visibleWhen: { key: 'noSafetyBriefing', notIn: [true] },
      },
      {
        key: 'safetyBriefingBy',
        label: 'Unterweisung durch',
        type: 'text',
        visibleWhen: { key: 'noSafetyBriefing', notIn: [true] },
      },
      { key: 'noSafetyBriefing', label: 'Keine Unterweisung erfolgt', type: 'bool3' },
      { key: 'safetyCheck', label: 'Sicherheitskontrolle', type: 'longtext' },
    ],
  },
  {
    key: 'technik',
    title: 'Technik vor Ort',
    description:
      'Anzahl Becken, Maße und Anlagentyp stehen am Projekt. Hier steht, was dort NICHT steht — die Angaben, die es nur vor Ort gibt.',
    fields: [
      {
        key: 'basinLabel',
        label: 'Becken (Bezeichnung/Nr.)',
        type: 'text',
        hint: 'z. B. „Becken 1+2" oder „NKB Ost"',
      },
      { key: 'scrapersPerBasin', label: 'Räumer je Becken', type: 'number', isInteger: true },
      { key: 'barsPerBasin', label: 'Balken je Becken', type: 'number', isInteger: true },
      { key: 'chainType', label: 'Kettentyp', type: 'enum', options: CHAIN_TYPES },
      // Ohne Kettentyp gibt es weder Teilung noch Antriebskette.
      {
        key: 'chainPitch',
        label: 'Teilung',
        type: 'text',
        hint: 'z. B. 133 mm',
        visibleWhen: { key: 'chainType', isSet: true },
      },
      {
        key: 'driveChain',
        label: 'Antriebskette',
        type: 'text',
        visibleWhen: { key: 'chainType', isSet: true },
      },
      { key: 'medium', label: 'Medium', type: 'enum', options: MEDIA },
      { key: 'mediumTemperatureC', label: 'Temperatur des Mediums', type: 'number', unit: '°C', isOffice: true },
      {
        key: 'isSeawater',
        label: 'Meerwasser',
        type: 'bool3',
        hint: 'Entscheidet über die Werkstoffwahl.',
      },
    ],
  },
  {
    key: 'zustand',
    title: 'Zustand & Umgebung',
    description: 'Wie das Becken angetroffen wurde — bestimmt Aufwand, Werkzeug und Zugang.',
    fields: [
      { key: 'basinShape', label: 'Beckenform', type: 'enum', options: BASIN_SHAPES },
      { key: 'basinMaterials', label: 'Material', type: 'multi', options: BASIN_MATERIALS },
      { key: 'buildType', label: 'Neubau/Umbau', type: 'enum', options: BUILD_TYPES },
      { key: 'hasParallelWalls', label: 'Wände parallel', type: 'bool3' },
      { key: 'hasFloorWaves', label: 'Bodenwellen', type: 'bool3' },
      { key: 'floorClearanceMm', label: 'Bodenabstand', type: 'number', unit: 'mm' },
      { key: 'hasExpansionJoints', label: 'Dehnfugen', type: 'bool3' },
      { key: 'hasRamp', label: 'Rampe', type: 'bool3' },
      { key: 'hasPumpSump', label: 'Pumpensumpf', type: 'bool3' },
      { key: 'hasVoutes', label: 'Vouten', type: 'bool3' },
      { key: 'hasSand', label: 'Sand', type: 'bool3' },
      { key: 'hasBaffles', label: 'Prallbleche', type: 'bool3' },
      { key: 'chemicalLoad', label: 'Chemische Belastung', type: 'text', isOffice: true },
      { key: 'cleanliness', label: 'Reinigungsgrad', type: 'enum', options: CLEANLINESS },
      { key: 'isFilled', label: 'Becken gefüllt', type: 'bool3' },
      { key: 'isCovered', label: 'Überdachung', type: 'bool3' },
      {
        key: 'isEnclosedSpace',
        label: 'Geschlossener Raum',
        type: 'bool3',
        hint: 'Befahrerlaubnis und Gaswarner nötig.',
      },
    ],
  },
  {
    key: 'betrieb',
    title: 'Betrieb',
    fields: [
      // Betreiberwissen — das liefert das Büro, nicht der Monteur am Becken.
      { key: 'operatingHoursPerDay', label: 'Betriebsdauer', type: 'number', unit: 'h/Tag', isOffice: true },
      { key: 'inOperationSince', label: 'Betrieb seit', type: 'date', isOffice: true },
      { key: 'lastServiceAt', label: 'Letzter Service', type: 'date', isOffice: true },
      { key: 'lastProbigMaintenanceAt', label: 'Letzte Wartung durch PROBIG', type: 'date', isOffice: true },
    ],
  },
];

/** Alle Felder über alle Abschnitte, in Reihenfolge. */
export const REPORT_FIELDS: readonly ReportField[] = REPORT_SECTIONS.flatMap((section) => section.fields);

/** Feld-Schlüssel → Definition (für PDF und Formular). */
export const REPORT_FIELD_BY_KEY: Readonly<Record<string, ReportField>> = Object.fromEntries(
  REPORT_FIELDS.map((field) => [field.key, field]),
);

/** Beschriftung eines Auswahlwerts; unbekannte Werte werden roh durchgereicht. */
export function reportOptionLabel(field: ReportField, value: string): string {
  return field.options?.find((option) => option.value === value)?.label ?? value;
}
